import React, { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { SITE_ASSETS } from '../constants/siteAssets';
import ActivitiesEventsLink from '../components/ActivitiesEventsLink';

const GOLD = '#A08D45';
const GOLD_HOVER = '#8a7938';
const NAVY = '#1c3244';
const CREAM = '#F9EFDD';
const CREAM_WASH = '#f7f5f0';

const FEATURES = [
  {
    image: SITE_ASSETS.features.guidedQuestions,
    title: 'Guided Questions',
    description: 'Curated prompts that draw out memories, values and life lessons.',
  },
  {
    image: SITE_ASSETS.features.recordYourWay,
    title: 'Record Your Way',
    description: 'Answer by video, audio or text — mix formats across questions.',
  },
  {
    image: SITE_ASSETS.features.automaticallyEdited,
    title: 'Automatically Edited',
    description: 'Responses stitched together into a polished legacy film.',
  },
  {
    image: SITE_ASSETS.features.saveAndShare,
    title: 'Save and Share',
    description: 'Download your video, or share a private link with loved ones.',
  },
];

const BENEFITS = [
  {
    image: SITE_ASSETS.photos.familyPortrait,
    title: 'Keep family stories alive',
    description: 'Capture the voices and memories that matter before they fade.',
  },
  {
    image: SITE_ASSETS.photos.legacyTree,
    title: 'Connect generations',
    description: 'Build a lasting bridge between past, present and future.',
  },
  {
    image: SITE_ASSETS.photos.grandmother,
    title: 'Share love that lasts',
    description: 'Give loved ones something meaningful they can return to anytime.',
  },
];

const STEPS = [
  {
    number: '01',
    title: 'Sign up',
    description: 'Create a free account in under a minute. No credit card required.',
  },
  {
    number: '02',
    title: 'Record your story',
    description: 'Follow guided questions and answer by video, audio or text.',
  },
  {
    number: '03',
    title: 'Save and share',
    description: 'Receive a finished video to download, keep private or share.',
  },
];

const StartFreeButton = ({ className = '', onClick, variant = 'solid' }) => {
  const isGhost = variant === 'ghost';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center px-8 py-3.5 text-sm sm:text-base font-button font-medium uppercase tracking-wide border transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#A08D45] ${className}`}
      style={{
        backgroundColor: isGhost ? 'transparent' : GOLD,
        borderColor: isGhost ? CREAM : GOLD,
        color: isGhost ? CREAM : '#ffffff',
        borderRadius: 0,
        fontFamily: "'Open Sans', sans-serif",
      }}
      onMouseEnter={(e) => {
        if (isGhost) {
          e.currentTarget.style.backgroundColor = 'rgba(249,239,221,0.12)';
        } else {
          e.currentTarget.style.backgroundColor = GOLD_HOVER;
          e.currentTarget.style.borderColor = GOLD_HOVER;
        }
      }}
      onMouseLeave={(e) => {
        if (isGhost) {
          e.currentTarget.style.backgroundColor = 'transparent';
        } else {
          e.currentTarget.style.backgroundColor = GOLD;
          e.currentTarget.style.borderColor = GOLD;
        }
      }}
    >
      Start Free
    </button>
  );
};

const galleryVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const galleryItem = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
  },
};

const Landing = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  useEffect(() => {
    if (videoRef.current && isVideoLoaded) {
      videoRef.current.play().catch(() => {});
    }
  }, [isVideoLoaded]);

  const goRegister = () => navigate('/register');

  return (
    <div className="flex flex-col min-h-[100dvh] bg-white">
      <Header />

      <main className="flex-1 pt-24">
        {/* Hero — Benefits */}
        <section
          id="benefits"
          className="relative min-h-[100dvh] w-full overflow-hidden scroll-mt-24 -mt-24 pt-24"
          aria-label="Benefits"
        >
          <div className="absolute inset-0 w-full h-full">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              onLoadedData={() => setIsVideoLoaded(true)}
              aria-hidden="true"
            >
              <source src="/Assets/sizzleintro.mp4" type="video/mp4" />
            </video>
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(105deg, rgba(28,50,68,0.88) 0%, rgba(28,50,68,0.62) 48%, rgba(28,50,68,0.4) 100%)',
              }}
            />
          </div>

          <div className="relative z-10 min-h-[calc(100dvh-6rem)] flex items-center px-6 sm:px-10 lg:px-16 py-16 sm:py-20">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-xl w-full"
              style={{
                backgroundColor: 'rgba(28,50,68,0.72)',
                border: `1px solid ${GOLD}55`,
                padding: '2rem 1.75rem',
              }}
            >
              <p
                className="font-serif italic text-lg sm:text-xl mb-3 tracking-wide"
                style={{ color: CREAM, fontWeight: 400 }}
              >
                Preserve what matters
              </p>
              <p
                className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-4 sm:mb-5 tracking-tight leading-none"
                style={{ fontWeight: 500, textShadow: '0 2px 12px rgba(0,0,0,0.35)' }}
              >
                Sizzle Legacies
              </p>
              <h1
                className="font-serif text-xl sm:text-2xl md:text-3xl text-white/95 leading-snug mb-5 sm:mb-6"
                style={{ fontWeight: 400 }}
              >
                Memories, stories and life lessons — recorded for the people you love.
              </h1>
              <p
                className="text-sm sm:text-base text-white/80 mb-8 sm:mb-10 max-w-md leading-relaxed"
                style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
              >
                Too many family stories disappear when they are never recorded. Capture them now.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <StartFreeButton onClick={goRegister} />
                  <ActivitiesEventsLink variant="ghost" />
                </div>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-8 py-3.5 text-sm sm:text-base font-button font-medium uppercase tracking-wide border transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  style={{
                    borderColor: CREAM,
                    color: CREAM,
                    borderRadius: 0,
                    fontFamily: "'Open Sans', sans-serif",
                  }}
                >
                  Sign in
                </Link>
              </div>
            </motion.div>
          </div>

          <div className="relative z-10 border-t border-white/10 bg-[rgba(28,50,68,0.92)]">
            <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-12 sm:py-14">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10"
              >
                {BENEFITS.map((benefit) => (
                  <article key={benefit.title} className="text-center">
                    <div className="mx-auto mb-4 w-full max-w-[14rem] aspect-[4/5] overflow-hidden border border-[#A08D45]/40">
                      <img
                        src={benefit.image}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <h3
                      className="font-serif text-lg sm:text-xl text-white mb-2"
                      style={{ fontWeight: 400 }}
                    >
                      {benefit.title}
                    </h3>
                    <p
                      className="text-sm text-white/70 leading-relaxed max-w-xs mx-auto"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                    >
                      {benefit.description}
                    </p>
                  </article>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Cream band divider */}
        <div
          className="w-full py-5 sm:py-6 text-center"
          style={{ backgroundColor: CREAM }}
          aria-hidden="true"
        >
          <p
            className="font-serif text-sm sm:text-base uppercase tracking-[0.28em]"
            style={{ color: NAVY, fontWeight: 500 }}
          >
            The Features
          </p>
        </div>

        {/* Features gallery */}
        <section
          id="features"
          className="scroll-mt-24 py-20 sm:py-28"
          style={{ backgroundColor: CREAM_WASH }}
          aria-label="Features"
        >
          <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5 }}
              className="mb-14 sm:mb-16 max-w-2xl mx-auto text-center"
            >
              <h2
                className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight"
                style={{ color: NAVY, fontWeight: 400 }}
              >
                Features
              </h2>
              <p
                className="mt-4 text-base sm:text-lg text-neutral-600 max-w-xl mx-auto leading-relaxed"
                style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
              >
                Everything you need to capture a lasting legacy
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-8 lg:gap-6"
              variants={galleryVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-60px' }}
            >
              {FEATURES.map((feature) => (
                  <motion.div
                    key={feature.title}
                    variants={galleryItem}
                    className="flex flex-col items-center text-center"
                  >
                    <div className="w-full aspect-square flex items-center justify-center mb-5 p-3 sm:p-4">
                      <img
                        src={feature.image}
                        alt=""
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <h3
                      className="font-serif text-xl sm:text-2xl mb-2"
                      style={{ color: NAVY, fontWeight: 400 }}
                    >
                      {feature.title}
                    </h3>
                    <p
                      className="text-sm text-neutral-600 leading-relaxed max-w-[18rem]"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                    >
                      {feature.description}
                    </p>
                  </motion.div>
                ))}
            </motion.div>

            <div className="mt-14 sm:mt-16 flex flex-col sm:flex-row justify-center items-center gap-4">
              <StartFreeButton onClick={goRegister} />
              <ActivitiesEventsLink variant="outline" />
            </div>
          </div>
        </section>

        {/* How it works — asymmetric split */}
        <section
          id="how-it-works"
          className="scroll-mt-24"
          aria-label="How it works"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[28rem]">
            {/* Left — navy editorial panel */}
            <div
              className="relative flex flex-col justify-center px-6 sm:px-10 lg:px-14 xl:px-16 py-16 sm:py-20"
              style={{ backgroundColor: NAVY }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.55 }}
              >
                <p
                  className="font-serif italic text-lg sm:text-xl mb-2"
                  style={{ color: CREAM, fontWeight: 400 }}
                >
                  From memory to film
                </p>
                <h2
                  className="font-serif text-3xl sm:text-4xl md:text-5xl text-white leading-tight mb-10"
                  style={{ fontWeight: 400 }}
                >
                  How it works
                </h2>

                <ol className="space-y-8 list-none p-0 m-0 mb-10">
                  {STEPS.map((step, index) => (
                    <motion.li
                      key={step.number}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: '-40px' }}
                      transition={{ duration: 0.4, delay: index * 0.08 }}
                      className="flex gap-4 sm:gap-5"
                    >
                      <span
                        className="flex-shrink-0 font-serif text-2xl sm:text-3xl leading-none"
                        style={{ color: GOLD, fontWeight: 400 }}
                        aria-hidden="true"
                      >
                        {step.number}
                      </span>
                      <div>
                        <h3
                          className="font-serif text-lg sm:text-xl text-white mb-1"
                          style={{ fontWeight: 400 }}
                        >
                          {step.title}
                        </h3>
                        <p
                          className="text-sm text-white/70 leading-relaxed"
                          style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                        >
                          {step.description}
                        </p>
                      </div>
                    </motion.li>
                  ))}
                </ol>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <StartFreeButton onClick={goRegister} />
                  <ActivitiesEventsLink variant="ghost" />
                </div>
                  <p
                    className="text-sm text-white/60"
                    style={{ fontFamily: "'Open Sans', sans-serif" }}
                  >
                    Looking for plans?{' '}
                    <Link
                      to="/subscription"
                      className="underline underline-offset-2 hover:text-[#F9EFDD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A08D45]"
                      style={{ color: GOLD }}
                    >
                      See pricing
                    </Link>
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right — photography */}
            <div className="relative min-h-[22rem] lg:min-h-full overflow-hidden">
              <img
                src={SITE_ASSETS.photos.familyJourney}
                alt="A family walking together toward a bright future"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(28,50,68,0.15) 0%, rgba(28,50,68,0.45) 100%)',
                }}
              />
              <p
                className="absolute bottom-8 left-8 right-8 font-serif text-2xl sm:text-3xl leading-tight text-white"
                style={{ fontWeight: 400, textShadow: '0 2px 12px rgba(0,0,0,0.45)' }}
              >
                Stories worth keeping
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Landing;
